var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { typography, focusVisible } from '../../../../styles/style-mixins';
const defaultTabStyles = ({ theme }) => css `
  padding: ${theme.spacings.kilo} ${theme.spacings.tera};
  color: var(--cui-fg-subtle);
  text-decoration: none;
  cursor: pointer;
  background-color: var(--cui-bg-normal);
  border: none;
  white-space: nowrap;
  height: 100%;
  align-items: center;
  float: left;
  display: flex;
  flex-direction: column;
  justify-content: center;
  outline: none;

  &:hover {
    background-color: var(--cui-bg-normal-hovered);
  }

  &:active {
    background-color: var(--cui-bg-normal-pressed);
  }
`;
const selectedTabStyles = ({ theme, selected }) => selected &&
    css `
    position: relative;
    color: var(--cui-fg-normal);

    &::after {
      content: ' ';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: ${theme.spacings.bit};
      background: var(--cui-border-accent);
    }
  `;
const tabIndex = (selected) => (selected ? undefined : '-1');
const StyledTab = styled('button')(typography('one'), focusVisible, defaultTabStyles, selectedTabStyles);
/**
 * Tab component that represents a single tab inside a Tabs wrapper
 */
const Tab = forwardRef((_a, ref) => {
    var { selected = false } = _a, props = __rest(_a, ["selected"]);
    return (_jsx(StyledTab, Object.assign({ ref: ref, role: "tab", selected: selected, "aria-selected": selected, tabIndex: tabIndex(selected) }, props)));
});
Tab.displayName = 'Tab';
Tab.propTypes = {
    /**
     * Triggers selected styles of the component
     */
    selected: PropTypes.bool,
};
/**
 * @component
 */
export default Tab;
